import { Link, useNavigate } from "react-router-dom";
import { Heart, Download } from "lucide-react";
import { toast } from "sonner";
import { FormatBadge, Stars } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { media, naira, errMsg } from "@/lib/api";

export default function TemplateCard({ t, index = 0 }) {
  const { user, favIds, toggleFav } = useAuth();
  const nav = useNavigate();
  const fav = favIds.has(t.id);

  const onFav = async (e) => {
    e.preventDefault();
    if (!user) return nav("/login");
    try {
      const on = await toggleFav(t.id);
      toast.success(on ? "Saved to favorites" : "Removed from favorites");
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <Link to={`/template/${t.id}`} data-testid={`template-card-${t.id}`}
      className="card-surface lift group relative flex flex-col overflow-hidden fade-up" style={{ animationDelay: `${index * 50}ms` }}>
      <div className="relative aspect-[4/5] overflow-hidden bg-secondary">
        <img src={media(t.cover_image)} alt={t.title} loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute left-2.5 top-2.5 flex flex-wrap gap-1 rounded-lg bg-background/85 p-1 backdrop-blur">
          {t.file_formats.slice(0, 3).map((f) => <FormatBadge key={f} f={f} />)}
        </div>
        <button data-testid={`fav-btn-${t.id}`} onClick={onFav} aria-label="Favorite"
          className="absolute right-2.5 top-2.5 grid h-9 w-9 place-items-center rounded-full bg-background/80 backdrop-blur transition-transform hover:scale-110">
          <Heart size={16} className={fav ? "fill-rose-500 text-rose-500" : ""} />
        </button>
      </div>
      <div className="flex flex-1 flex-col gap-2 p-3.5 sm:p-4">
        <h3 className="line-clamp-1 font-sans text-sm font-semibold sm:text-[15px]" data-testid="template-card-title">{t.title}</h3>
        <p className="line-clamp-1 text-xs text-muted-foreground">By {t.seller_name}</p>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <Stars value={t.rating_avg} count={t.rating_count} size={12} />
          <span className="inline-flex items-center gap-1"><Download size={12} />{t.sales_count} sales</span>
        </div>
        <div className="mt-auto flex items-center justify-between pt-2">
          <span className="font-display text-lg font-bold" data-testid="template-card-price">{naira(t.price)}</span>
          <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium transition-colors group-hover:bg-primary group-hover:text-white">View</span>
        </div>
      </div>
    </Link>
  );
}

export const TemplateGrid = ({ items }) => (
  <div className="grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 xl:grid-cols-4">
    {items.map((t, i) => <TemplateCard key={t.id} t={t} index={i} />)}
  </div>
);
